import
{
    ValidatorFn, AbstractControl, Validators, AsyncValidatorFn, ValidationErrors
} from "@angular/forms";

import { ValueOrGetter, evaluate } from "./model";
import { compareTimes, parseLgdParsedTime } from "./timeFunctions";

export class LgdValidators
{
    private static isEmptyInputValue(value: { length: number; }): boolean
    {
        // we don't check for string here so it also works with arrays
        return value === undefined || value == null || value.length === 0;
    }

    /**
     * @returns a validator that will apply `Validators.required` if the given predicate
     *
     * @see `Validators.required` from `@angular/forms`
     */
    static getRequiredValidator(required?: ValueOrGetter<boolean>): ValidatorFn
    {
        return isRequiredValidator;

        function isRequiredValidator(control: AbstractControl): ValidationErrors | null
        {
            if (!!required && evaluate(required))
            {
                return Validators.required(control);
            }
            return null;
        }
    }

    /**
     * @returns a validator that will apply `Validators.required` if the given predicate
     *
     * @see `Validators.required` from `@angular/forms`
     */
    static getNumberRequiredValidator(required?: ValueOrGetter<boolean>): ValidatorFn
    {
        return isNumberRequiredValidator;

        function isNumberRequiredValidator(control: AbstractControl): ValidationErrors | null
        {
            if (!!required && evaluate(required))
            {
                const value = LgdValidators.getNumber(control.value);
                return value == null || isNaN(value) ? { required: true } : null;
            }
            return null;
        }
    }

    /**
     * @returns a validator that will test if a control's value is greater than or equal to the given value.
     */
    static getMinValidator(min?: ValueOrGetter<number>): ValidatorFn
    {
        return minValidator;

        function minValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _min = evaluate(min);
            if (_min)
            {
                if (LgdValidators.isEmptyInputValue(control.value) || _min === undefined || _min == null)
                {
                    return null;
                }
                const value = LgdValidators.getNumber(control.value);
                return !isNaN(value) && value < _min ? {
                    min: {
                        min: _min,
                        actual: control.value
                    }
                } : null;
            }
            return null;
        }
    }

    /**
     * @returns a validator that will test if a control's value is less than or equal to the given value.
     */
    static getMaxValidator(max?: ValueOrGetter<number>): ValidatorFn
    {
        return maxValidator;

        function maxValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _max = evaluate(max);
            if (_max)
            {
                if (LgdValidators.isEmptyInputValue(control.value) || _max === undefined || _max == null)
                {
                    return null;
                }

                const value = LgdValidators.getNumber(control.value);
                return !isNaN(value) && value > _max ? {
                    max: {
                        max: _max,
                        actual: control.value
                    }
                } : null;
            }
            return null;
        }
    }

    /**
     * @returns a validator that will test if a control's value is greater than or equal to the given value.
     */
    static getMinTimeValidator(min?: ValueOrGetter<string>): ValidatorFn
    {
        return minValidator;

        function minValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _min = evaluate(min);
            if (_min)
            {
                if (LgdValidators.isEmptyInputValue(control.value) || LgdValidators.isEmptyInputValue(_min))
                {
                    return null;
                }
                const minTime = parseLgdParsedTime(_min);
                const value = parseLgdParsedTime(control.value);
                return compareTimes(minTime, value) < 1 ? {
                    min: {
                        min: _min,
                        actual: control.value
                    }
                } : null;
            }
            return null;
        }
    }

    /**
     * @returns a validator that will test if a control's value is less than or equal to the given value.
     */
    static getMaxTimeValidator(max?: ValueOrGetter<string>): ValidatorFn
    {
        return maxValidator;

        function maxValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _max = evaluate(max);
            if (_max)
            {
                if (LgdValidators.isEmptyInputValue(control.value) || LgdValidators.isEmptyInputValue(_max))
                {
                    return null;
                }

                const maxTime = parseLgdParsedTime(_max);
                const value = parseLgdParsedTime(control.value);
                return compareTimes(value, maxTime) < 1 ? {
                    max: {
                        max: _max,
                        actual: control.value
                    }
                } : null;
            }
            return null;
        }
    }

    static getPrecisionValidator(precision?: ValueOrGetter<number>): ValidatorFn
    {
        return precisionValidator;

        function precisionValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _precision = evaluate(precision);
            if (_precision)
            {
                if (LgdValidators.isEmptyInputValue(control.value) || _precision === undefined || _precision == null)
                {
                    return null;
                }

                const value = LgdValidators.getNumber(control.value);
                return !isNaN(value) && (value.toString().match(/\d/g) || []).length > _precision
                    ? {
                        precision: {
                            precision: _precision,
                            actual: control.value
                        }
                    }
                    : null;
            }
            return null;
        }
    }

    static getScaleValidator(scale?: ValueOrGetter<number>): ValidatorFn
    {
        return scaleValidator;

        function scaleValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _scale = evaluate(scale);
            if (_scale)
            {
                if (LgdValidators.isEmptyInputValue(control.value) || _scale === undefined || _scale == null)
                {
                    return null;
                }

                const value = LgdValidators.getNumber(control.value);
                const parts = value.toString().split(".");
                const fractional = parts[1] || "";

                return !isNaN(value) && fractional.length > _scale
                    ? {
                        scale: {
                            scale: _scale,
                            actual: control.value
                        }
                    }
                    : null;
            }

            return null;
        }
    }

    private static getNumber(value?: string | number): number | null
    {
        return value === undefined || value === null
            ? null
            : typeof value === "number"
                ? value
                : parseFloat(value.replace(/[^\d.-]/g, ""));
    }

    /**
     * @returns a validator that will test that the length of a control's value is greater than or equal to the given value.
     */
    static getMinLengthValidator(min?: ValueOrGetter<number>): ValidatorFn
    {
        return minLengthValidator;

        function minLengthValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _min = evaluate(min);
            if (_min)
            {
                return Validators.minLength(_min)(control);
            }
            return null;
        }
    }

    /**
     * @returns a validator that will test that the length of a control's value is less than or equal to the given value.
     */
    static getMaxLengthValidator(max?: ValueOrGetter<number>): ValidatorFn
    {
        return maxLengthValidator;

        function maxLengthValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _max = evaluate(max);
            if (_max)
            {
                return Validators.maxLength(_max)(control);
            }
            return null;
        }
    }

    /**
     * @returns a validator that will test a control's value against the given regular expression.
     */
    static getPatternValidator(pattern?: ValueOrGetter<RegExp>): ValidatorFn
    {
        return matchesPatternValidator;

        function matchesPatternValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _pattern = evaluate(pattern);
            return Validators.pattern(_pattern)(control);
        }
    }

    static getIsNumberValidator(): ValidatorFn
    {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const VALID_NUMBER_PATTERN = /^-?(\d{1,3}(,?\d{3})*)?(\.\d*)?$/;
        const stringValidator = LgdValidators.getPatternValidator(VALID_NUMBER_PATTERN);
        return isNumber;

        function isNumber(control: AbstractControl): ValidationErrors | null
        {
            const value = control.value;
            if (LgdValidators.isEmptyInputValue(value) || typeof value === "number") return null;

            return stringValidator(control);
        }
    }

    static getIsCurrencyValidator(): ValidatorFn
    {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const VALID_CURRENCY_PATTERN = /^[$-]{0,2}(\d{1,3}(,?\d{3})*)?(\.\d*)?$/;
        const stringValidator = LgdValidators.getPatternValidator(VALID_CURRENCY_PATTERN);
        return isCurrency;

        function isCurrency(control: AbstractControl): ValidationErrors | null
        {
            const value = control.value;
            if (LgdValidators.isEmptyInputValue(value) || typeof value === "number") return null;

            return stringValidator(control);
        }
    }

    /**
     * @param maxSize Maximum file size in bytes.
     * @returns a validator that will test that the size of the File of a control's value is less than or equal to the given value.
     */
    static getMaxFileSizeValidator(maxSize?: ValueOrGetter<number>): ValidatorFn
    {
        return maxFileSizeValidator;

        function maxFileSizeValidator(control: AbstractControl): ValidationErrors | null
        {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const _maxSize = evaluate(maxSize);
            if (_maxSize)
            {
                if (LgdValidators.isEmptyInputValue(control.value) || _maxSize === undefined || _maxSize == null)
                {
                    return null;
                }

                const value = LgdValidators.getNumber(control.value.size);
                return !isNaN(value) && value > _maxSize
                    ? {
                        max: {
                            max: _maxSize,
                            actual: control.value.size
                        }
                    }
                    : null;
            }
            return null;
        }
    }
}

export abstract class LgdCustomValidator
{
    abstract getValidatorFn(): ValidatorFn;
    abstract getErrorMessage(label: string): ReadonlyArray<CustomValidatorMessage>;
}

export abstract class LgdCustomAsyncValidator
{
    abstract getValidatorFn(): AsyncValidatorFn;
    abstract getErrorMessage(label: string): ReadonlyArray<CustomValidatorMessage>;
}

export interface CustomValidatorMessage
{
    readonly errorKey: string;
    readonly message: string;
}

export class EmailValidator extends LgdCustomValidator
{
    getValidatorFn(): ValidatorFn
    {
        return Validators.email;
    }

    getErrorMessage(label: string): ReadonlyArray<CustomValidatorMessage>
    {
        return [{
            errorKey: "email",
            message: `${label} is not a valid email address.`,
        },];
    }
}

export abstract class LgdPatternValidator extends LgdCustomValidator
{
    constructor(protected readonly pattern: string | RegExp)
    {
        super();
    }

    getValidatorFn(): ValidatorFn
    {
        return Validators.pattern(this.pattern);
    }

    getErrorMessage(label: string): ReadonlyArray<CustomValidatorMessage>
    {
        return [{
            errorKey: "pattern",
            message: `${label} is invalid.`,
        },];
    }
}

/**
 * Defines a Validator and matching message requiring the value of the FormControl to match the pattern like "(999) 999-9999x9999999".
 * TODO - This might not be a SYSTEM-wide standard. It was implemented for the UB apps.
 */
export class PhoneValidator extends LgdPatternValidator
{
    constructor()
    {
        //(999) 999-9999x9999999
        super(/^\+?\(?[0-9]{3}\)?([-\s.]{1,3})?[0-9]{3}[-\s.]?[0-9]{4}?[x]?([0-9]?){7}$/);
    }

    /** @override */
    getErrorMessage(label: string): ReadonlyArray<CustomValidatorMessage>
    {
        return [{
            errorKey: "pattern",
            message: `${label} is invalid. ${label} should be formatted like '(999) 999-9999x9999999'`,
        },];
    }
}

/**
 * TODO - This might not be a SYSTEM-wide standard. It was implemented for the UB apps.
 */
export class SocialSecurityValidator extends LgdPatternValidator
{
    // 123-12-1234
    constructor()
    {
        super(/^$|^[0-9]{3}[-]?[0-9]{2}[-]?[0-9]{4}$/);
    }
}

export class MaskedSocialSecurityValidator extends LgdPatternValidator
{
    // 123-12-1234 OR XXX-XX-1234
    constructor()
    {
        super(/^$|^[0-9]{3}[-]?[0-9]{2}[-]?[0-9]{4}$|^[X]{3}[-]?[X]{2}[-]?[0-9]{4}$/);
    }
}

/**
 * TODO - This might not be a SYSTEM-wide standard. It was implemented for apps/vendormanager.
 */
export class UrlValidator extends LgdPatternValidator
{
    constructor()
    {
        super(/^[a-zA-Z0-9\-.]+\.[a-zA-Z](:[a-zA-Z0-9]*)?\/?([a-zA-Z0-9\-._?,'/\\+&amp;%$#=~])*[^.,)(\s]$/);
    }
}
